import React, { useState } from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import {
  alpha,
  darken,
  keyframes,
  lighten,
  useTheme,
} from "@mui/material/styles";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { companyLogoUrl } from "../../../apis/company-logo-apis/companyLogoApis";
import { PAGE_MAX_WIDTH } from "../styles/publicStyles";
import { brandOf, heroTitleOf } from "../data/brand";

// The top of the Overview: who the company is, how to reach them, and one
// line of live status. Kept short (like Delve's) so the frameworks and
// documents below are in view; the other tabs have no banner.
//
// The company picks the look (like Vanta's Trust Center "Customize", see
// data/brand.js): a gradient of two colours, an image, or no banner; and
// light or dark text (chosen automatically to read on the banner unless
// they set it).

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.8); }
`;

// Colours of everything on the banner, for light text (on a dark banner) or
// dark text (on a light banner, or no banner).
export const toneOf = (tone, t) =>
  tone === "light"
    ? {
        onDark: true,
        text: "#FFFFFF",
        soft: "rgba(255, 255, 255, 0.8)",
        line: "rgba(255, 255, 255, 0.22)",
        fill: "rgba(255, 255, 255, 0.10)",
        hover: "rgba(255, 255, 255, 0.18)",
        live: "#4ADE9B",
      }
    : {
        onDark: false,
        text: t.ink,
        soft: t.inkSoft,
        line: alpha(t.ink, 0.16),
        fill: alpha("#FFFFFF", 0.7),
        hover: "#FFFFFF",
        live: t.verified,
      };

const LiveDot = ({ color }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
      backgroundColor: color,
      animation: `${pulse} 2s ease-in-out infinite`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }}
  />
);

// "N controls are continuously monitored by Regodit". The count opens the
// Controls tab. Not shown when no controls are shared.
const LiveStatus = ({ data, onOpenTab, tone }) => {
  const theme = useTheme();
  const count = (data.controls || []).length;
  if (count === 0) return null;
  return (
    <Stack
      component="section"
      aria-label="Live security status"
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        px: 1.5,
        py: 0.5,
        minHeight: 36,
        borderRadius: theme.trust.radius.pill,
        border: `1px solid ${tone.line}`,
        backgroundColor: tone.fill,
        backdropFilter: "blur(6px)",
        maxWidth: "100%",
      }}
    >
      <LiveDot color={tone.live} />
      <Box sx={{ minWidth: 0, fontSize: 13.5, color: tone.soft }}>
        <Link
          component="button"
          type="button"
          onClick={() => onOpenTab("controls")}
          underline="hover"
          sx={{
            color: tone.text,
            fontWeight: 500,
            fontSize: 13.5,
            verticalAlign: "baseline",
          }}
        >
          {count} {count === 1 ? "control is" : "controls are"}
        </Link>{" "}
        continuously monitored by Regodit
      </Box>
    </Stack>
  );
};

// Contact links on the banner: "glass" pills that read on any image.
const ContactLink = ({ href, icon: Icon, children, external = true, tone }) => {
  const theme = useTheme();
  const t = theme.trust;
  return (
    <Link
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener" : undefined}
      underline="none"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        maxWidth: "100%",
        px: 1.25,
        py: 0.625,
        borderRadius: t.radius.pill,
        border: `1px solid ${tone.line}`,
        backgroundColor: tone.fill,
        backdropFilter: "blur(6px)",
        color: tone.text,
        fontSize: 13.5,
        fontWeight: 500,
        transition: `background-color 160ms ${t.ease}, border-color 160ms ${t.ease}`,
        "&:hover": { backgroundColor: tone.hover },
        "&:focus-visible": {
          outline: `2px solid ${tone.text}`,
          outlineOffset: 2,
        },
      }}
    >
      <Icon
        aria-hidden="true"
        sx={{ fontSize: 16, color: tone.soft, flexShrink: 0 }}
      />
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </Box>
    </Link>
  );
};

const OVERVIEW_LINES = 2;

// What sits behind the banner.
// - image: the admin's image under a wash of the primary colour, dark for
//   light text or pale for dark text, so the text reads on any photo;
// - gradient: the admin's two colours, or (none chosen) deep shades of the
//   primary for light text / pale ones for dark text;
// - minimal: nothing (the title sits on the page).
export const bannerBackground = (t, brand, imageUrl) => {
  if (brand.bannerStyle === "minimal") return {};
  const light = brand.textTone === "light";
  const deep = light ? darken(t.accent, 0.55) : lighten(t.accent, 0.86);
  const deeper = light ? darken(t.accent, 0.78) : lighten(t.accent, 0.94);
  if (brand.bannerStyle === "image" && imageUrl)
    return {
      backgroundColor: deeper,
      backgroundImage: `linear-gradient(100deg, ${alpha(deeper, 0.92)} 0%, ${alpha(deeper, 0.7)} 48%, ${alpha(deeper, 0.32)} 100%), url("${imageUrl}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  if (brand.gradient)
    return {
      backgroundColor: brand.gradient[0],
      backgroundImage: `linear-gradient(120deg, ${brand.gradient[0]} 0%, ${brand.gradient[1]} 100%)`,
    };
  return {
    backgroundColor: deep,
    backgroundImage: [
      `radial-gradient(900px 420px at 88% -10%, ${alpha(light ? lighten(t.accent, 0.1) : lighten(t.accent, 0.6), 0.6)}, transparent 62%)`,
      `radial-gradient(700px 380px at -5% 115%, ${alpha(light ? t.accent : lighten(t.accent, 0.7), 0.4)}, transparent 60%)`,
      `linear-gradient(135deg, ${deep} 0%, ${deeper} 100%)`,
    ].join(", "),
  };
};

const PublicHero = ({ data, onOpenTab }) => {
  const theme = useTheme();
  const t = theme.trust;
  const { company } = data;
  const [expanded, setExpanded] = useState(false);
  const overview = (company.overview || "").trim();
  const longOverview = overview.length > 160;
  const heroUrl = companyLogoUrl(company.heroImagePath);
  const brand = brandOf(company);
  const tone = toneOf(brand.textTone, t);
  const minimal = brand.bannerStyle === "minimal";

  const links = [
    company.securityEmail && {
      key: "email",
      href: `mailto:${company.securityEmail}`,
      icon: MailOutlineIcon,
      label: company.securityEmail,
      external: false,
    },
    company.website && {
      key: "website",
      href: company.website,
      icon: LanguageOutlinedIcon,
      label: company.website.replace(/^https?:\/\//, "").replace(/\/$/, ""),
    },
    company.privacyUrl && {
      key: "privacy",
      href: company.privacyUrl,
      icon: DescriptionOutlinedIcon,
      label: "Privacy policy",
    },
    company.termsUrl && {
      key: "terms",
      href: company.termsUrl,
      icon: DescriptionOutlinedIcon,
      label: "Terms of service",
    },
    ...(company.customLinks || []).map((link) => ({
      key: link.id,
      href: link.url,
      icon: LinkOutlinedIcon,
      label: link.label,
    })),
  ].filter(Boolean);

  return (
    <Box
      component="header"
      // Same box as the content below, so the banner lines up with it.
      sx={{
        // width: the page is a flex column, where a centred box would
        // otherwise shrink to its text.
        width: "100%",
        maxWidth: PAGE_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, sm: 3, lg: 4 },
        pt: { xs: 1.5, md: 2.5 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          ...bannerBackground(t, brand, heroUrl),
          borderRadius: { xs: "16px", md: "20px" },
          overflow: "hidden",
          color: tone.text,
          // No banner: the text lines up with the content below.
          px: minimal ? 0 : { xs: 2.5, sm: 4, md: 5 },
          py: minimal ? { xs: 1.5, md: 2 } : { xs: 3, sm: 4, md: 4.5 },
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 26, sm: 32, md: 36 },
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.12,
            color: tone.text,
            textWrap: "balance",
          }}
        >
          {heroTitleOf(company)}
        </Typography>
        {overview && (
          <Box sx={{ mt: 1.5, maxWidth: 720 }}>
            <Typography
              sx={{
                fontSize: 15,
                lineHeight: 1.6,
                color: tone.soft,
                whiteSpace: "pre-line",
                ...(expanded || !longOverview
                  ? {}
                  : {
                      display: "-webkit-box",
                      WebkitLineClamp: OVERVIEW_LINES,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }),
              }}
            >
              {overview}
            </Typography>
            {longOverview && (
              <Link
                component="button"
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                sx={{
                  mt: 0.5,
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: tone.text,
                  textDecorationColor: tone.line,
                }}
              >
                {expanded ? "Show less" : "Read more"}
              </Link>
            )}
          </Box>
        )}

        {/* Contact links on the left, live status on the right (stacked on
            phones). */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={{ xs: 2, md: 3 }}
          sx={{ mt: 2.5 }}
        >
          {(links.length > 0 || company.dataResidency) && (
            <Stack
              direction="row"
              useFlexGap
              spacing={1}
              sx={{ flexWrap: "wrap", minWidth: 0 }}
            >
              {links.map((link) => (
                <ContactLink
                  key={link.key}
                  href={link.href}
                  icon={link.icon}
                  external={link.external !== false}
                  tone={tone}
                >
                  {link.label}
                </ContactLink>
              ))}
              {company.dataResidency && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{
                    px: 1.25,
                    py: 0.625,
                    color: tone.soft,
                    fontSize: 13.5,
                  }}
                >
                  <PublicOutlinedIcon
                    aria-hidden="true"
                    sx={{ fontSize: 16 }}
                  />
                  <span>
                    Data stored in <strong>{company.dataResidency}</strong>
                  </span>
                </Stack>
              )}
            </Stack>
          )}
          <Box sx={{ flexShrink: 0, maxWidth: "100%" }}>
            <LiveStatus data={data} onOpenTab={onOpenTab} tone={tone} />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default PublicHero;
